import { useEffect } from 'react';
import { queryClient } from '../queryClient';
import { coldRouteLoaders } from '../routes/coldRoutes';
import { prefetchBookkeepingClients } from './useClients';
import { prefetchClientReviews } from './useReviews';

// Module-level so remounts (auth redirects, StrictMode) never re-stream.
let streamStarted = false;

/** Test hook: reset the run-once latch. */
export function _resetBackgroundStreamForTests(): void {
  streamStarted = false;
}

/** requestIdleCallback with a setTimeout fallback (Safari). */
function onIdle(fn: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => fn(), { timeout: 2000 });
  } else {
    setTimeout(fn, 200);
  }
}

/**
 * Background streaming (the "keep loading after the essentials" phase): after the
 * dashboard's initial queries settle, drain — one item per idle slice — the hot
 * adjacent data (clients + reviews lists, each ONE Function execution) and then every
 * cold route chunk (static CDN assets, zero backend cost). After it completes, no
 * in-session navigation chunk-stalls, and the hot lists paint instantly.
 * Best-effort throughout: failures are swallowed; real navigation has its own fetches.
 *
 * The drain is module-scope background work, deliberately NOT tied to the component
 * lifetime: no effect cleanup, no cancellation. It survives StrictMode's dev-only
 * mount/cleanup/remount cycle (which would otherwise strand the run-once latch with
 * nothing running) and any legit remount, and dies naturally with the page. Every
 * queue item is best-effort and touches no component state, so nothing leaks.
 */
export function useBackgroundStream(): void {
  useEffect(() => {
    if (streamStarted) return;
    streamStarted = true;

    const queue: Array<() => Promise<unknown>> = [
      () => prefetchBookkeepingClients(queryClient),
      () => prefetchClientReviews(queryClient),
      ...coldRouteLoaders.map((r) => r.load),
    ];

    const drain = () => {
      const next = queue.shift();
      if (!next) return;
      Promise.resolve()
        .then(next)
        .catch(() => undefined)
        .finally(() => onIdle(drain));
    };

    // Poll (cheap) until the boot queries settle, then start draining on idle.
    const waitForSettle = () => {
      if (queryClient.isFetching() === 0) {
        onIdle(drain);
      } else {
        setTimeout(waitForSettle, 1000);
      }
    };
    setTimeout(waitForSettle, 1000); // debounce: let the dashboard fire its queries first
  }, []);
}
